import {Node, Edge, getIncomers, getOutgoers, getConnectedEdges} from 'reactflow'
import {NodeType, EDGE_TYPES, EDGE_LABELS} from '../../../../../../Utils/Constant/FlowConstants'

export type BranchType = 'yes' | 'no'

interface DeleteBranchOptions {
  nodes: Node[]
  edges: Edge[]
  conditionNodeId: string
  branchType: BranchType
}

interface DeleteBranchResult {
  updatedNodes: Node[]
  updatedEdges: Edge[]
  deletedNodes: string[]
  deletedEdges: string[]
}

/**
 * Finds all nodes that are part of any path from trigger to end
 */
const findPathNodes = (nodes: Node[], edges: Edge[]): Set<string> => {
  const pathNodes = new Set<string>()

  // Find trigger nodes (nodes with no incoming edges)
  const triggerNodes = nodes.filter((node) => {
    const incomers = getIncomers(node, nodes, edges)
    return incomers.length === 0
  })

  // Find end nodes (nodes with no outgoing edges)
  const endNodes = nodes.filter((node) => {
    const outgoers = getOutgoers(node, nodes, edges)
    return outgoers.length === 0
  })

  // Find all nodes in paths from any trigger to any end
  triggerNodes.forEach((triggerNode) => {
    endNodes.forEach((endNode) => {
      const visited = new Set<string>()
      const queue = [triggerNode.id]

      while (queue.length > 0) {
        const currentId = queue.shift()!
        if (currentId === endNode.id) {
          // Mark all visited nodes as part of a valid path
          visited.forEach((nodeId) => pathNodes.add(nodeId))
          break
        }

        if (visited.has(currentId)) continue
        visited.add(currentId)

        const outgoingEdges = edges.filter((edge) => edge.source === currentId)
        outgoingEdges.forEach((edge) => {
          if (!visited.has(edge.target)) {
            queue.push(edge.target)
          }
        })
      }
    })
  })

  return pathNodes
}

/**
 * Preserves parent node data and ensures parent nodes are not affected by deletion
 */
const preserveParentNodes = (
  nodes: Node[],
  edges: Edge[],
  conditionNodeId: string
): {parentNodes: Node[]; parentEdges: Edge[]} => {
  // Find all parent nodes (nodes that have edges pointing to the condition node)
  const parentEdges = edges.filter((edge) => edge.target === conditionNodeId)
  const parentNodeIds = Array.from(new Set(parentEdges.map((edge) => edge.source)))
  const parentNodes = nodes.filter((node) => parentNodeIds.includes(node.id))

  console.log('🚀 Preserving parent nodes:', parentNodeIds)
  console.log(
    '🚀 Preserving parent edges:',
    parentEdges.map((edge) => edge.id)
  )

  return {parentNodes, parentEdges}
}

/**
 * Finds all nodes and edges in a specific branch of a condition node
 */
const findBranchNodesAndEdges = (
  nodes: Node[],
  edges: Edge[],
  conditionNodeId: string,
  branchLabel: string
): {branchNodes: Node[]; branchEdges: Edge[]} => {
  const branchEdges = edges.filter(
    (edge) => edge.source === conditionNodeId && edge.data?.label === branchLabel
  )

  const branchNodes: Node[] = []
  const visited = new Set<string>()

  const traverseBranch = (nodeId: string) => {
    if (visited.has(nodeId)) return
    visited.add(nodeId)

    const node = nodes.find((n) => n.id === nodeId)
    if (node) {
      branchNodes.push(node)

      const outgoingEdges = edges.filter((edge) => edge.source === nodeId)
      outgoingEdges.forEach((edge) => {
        traverseBranch(edge.target)
      })
    }
  }

  branchEdges.forEach((edge) => {
    traverseBranch(edge.target)
  })

  return {branchNodes, branchEdges}
}

const findEndNodesInBranch = (
  nodes: Node[],
  edges: Edge[],
  conditionNodeId: string,
  branchLabel: string
): Node[] => {
  const branchEdges = edges.filter(
    (edge) => edge.source === conditionNodeId && edge.data?.label === branchLabel
  )

  const endNodes: Node[] = []
  const visited = new Set<string>()

  const traverseToEnd = (nodeId: string) => {
    if (visited.has(nodeId)) return
    visited.add(nodeId)

    const node = nodes.find((n) => n.id === nodeId)
    if (node) {
      if (node.type === NodeType.END) {
        endNodes.push(node)
      } else {
        // Continue traversal
        const outgoingEdges = edges.filter((edge) => edge.source === nodeId)
        outgoingEdges.forEach((edge) => {
          traverseToEnd(edge.target)
        })
      }
    }
  }

  branchEdges.forEach((edge) => {
    traverseToEnd(edge.target)
  })

  return endNodes
}

const findAllChildNodes = (
  nodes: Node[],
  edges: Edge[],
  startNodeId: string
): {childNodes: Node[]; childEdges: Edge[]} => {
  const childNodes: Node[] = []
  const childEdges: Edge[] = []
  const visited = new Set<string>()

  const traverseChildren = (nodeId: string) => {
    if (visited.has(nodeId)) return
    visited.add(nodeId)

    const node = nodes.find((n) => n.id === nodeId)
    if (node) {
      // Include ALL child nodes, including end nodes
      childNodes.push(node)

      // Find all outgoing edges
      const outgoingEdges = edges.filter((edge) => edge.source === nodeId)
      outgoingEdges.forEach((edge) => {
        childEdges.push(edge)
        traverseChildren(edge.target)
      })
    }
  }

  traverseChildren(startNodeId)
  return {childNodes, childEdges}
}

/**
 * Detects if a flow has a valid main path from trigger to end
 */
const hasValidMainPath = (nodes: Node[], edges: Edge[]): boolean => {
  // Find trigger nodes (nodes with no incoming edges)
  const triggerNodes = nodes.filter((node) => {
    const incomers = getIncomers(node, nodes, edges)
    return incomers.length === 0
  })

  const endNodes = nodes.filter((node) => {
    const outgoers = getOutgoers(node, nodes, edges)
    return outgoers.length === 0
  })

  for (const triggerNode of triggerNodes) {
    for (const endNode of endNodes) {
      const visited = new Set<string>()
      const queue = [triggerNode.id]

      while (queue.length > 0) {
        const currentId = queue.shift()!
        if (currentId === endNode.id) {
          return true
        }

        if (visited.has(currentId)) continue
        visited.add(currentId)

        const outgoingEdges = edges.filter((edge) => edge.source === currentId)
        outgoingEdges.forEach((edge) => {
          if (!visited.has(edge.target)) {
            queue.push(edge.target)
          }
        })
      }
    }
  }

  return false // No valid path found
}

/**
 * Finds nodes that are not part of any valid path from trigger to end
 */
const findDisconnectedNodes = (nodes: Node[], edges: Edge[]): Node[] => {
  const pathNodes = findPathNodes(nodes, edges)

  return nodes.filter((node) => {
    // Always keep trigger and end nodes
    if (node.type === NodeType.TRIGGER || node.type === NodeType.END) {
      return false
    }

    // Check if node is part of any valid path
    return !pathNodes.has(node.id)
  })
}

/**
 * Finds the last node in a path that can be connected to an end node
 */
const findLastConnectableNode = (nodes: Node[], edges: Edge[]): Node | null => {
  // Find all nodes that have outgoing edges
  const nodesWithOutgoing = nodes.filter((node) => {
    const outgoers = getOutgoers(node, nodes, edges)
    return outgoers.length > 0
  })

  // Find nodes that are not targets of any edge (potential end points)
  const potentialEndPoints = nodes.filter((node) => {
    const incomers = getIncomers(node, nodes, edges)
    return incomers.length === 0
  })

  // If we have potential end points, return the first one
  if (potentialEndPoints.length > 0) {
    return potentialEndPoints[0]
  }

  // Otherwise, find the last node in the flow (node with highest position or last in array)
  if (nodes.length > 0) {
    return nodes[nodes.length - 1]
  }

  return null
}

/**
 * Ensures the flow has a valid main path by connecting to existing end node or creating one
 */
const ensureMainPathExists = (
  nodes: Node[],
  edges: Edge[]
): {updatedNodes: Node[]; updatedEdges: Edge[]; createdEndNode?: Node; createdEdge?: Edge} => {
  console.log('🚀 Checking if flow has valid main path...')

  if (hasValidMainPath(nodes, edges)) {
    console.log('🚀 Flow already has valid main path')
    return {updatedNodes: nodes, updatedEdges: edges}
  }

  console.log('🚀 No valid main path detected, attempting to fix...')

  // Find existing end nodes
  const existingEndNodes = nodes.filter((node) => {
    const outgoers = getOutgoers(node, nodes, edges)
    return outgoers.length === 0
  })

  // Find trigger nodes
  const triggerNodes = nodes.filter((node) => {
    const incomers = getIncomers(node, nodes, edges)
    return incomers.length === 0
  })

  let targetEndNode: Node | null = null
  let sourceNode: Node | null = null

  if (existingEndNodes.length > 0) {
    targetEndNode = existingEndNodes[0]
    console.log('🚀 Found existing end node:', targetEndNode.id)
  } else {
    const endNodeId = `end-${Date.now()}`
    targetEndNode = {
      id: endNodeId,
      type: NodeType.END,
      position: {x: 0, y: 0},
      data: {label: 'End', deletable: true},
    }
    console.log('🚀 Created new end node:', endNodeId)
  }

  if (triggerNodes.length > 0) {
    sourceNode = findLastConnectableNode(nodes, edges)
  } else {
    sourceNode = nodes.length > 0 ? nodes[nodes.length - 1] : null
  }

  if (!sourceNode) {
    console.warn('🚀 No source node found to connect to end node')
    return {updatedNodes: nodes, updatedEdges: edges}
  }

  const existingConnection = edges.find(
    (edge) => edge.source === sourceNode!.id && edge.target === targetEndNode!.id
  )

  if (existingConnection) {
    console.log('🚀 Connection already exists between', sourceNode.id, 'and', targetEndNode.id)
    return {updatedNodes: nodes, updatedEdges: edges}
  }

  // Create connection edge
  const connectionEdge: Edge = {
    id: `connection-${sourceNode.id}-${targetEndNode.id}`,
    source: sourceNode.id,
    target: targetEndNode.id,
    type: EDGE_TYPES.SMOOTHSTEP,
    data: {},
  }

  console.log('🚀 Created connection from', sourceNode.id, 'to', targetEndNode.id)

  const updatedNodes = targetEndNode.id.startsWith('end-') ? [...nodes, targetEndNode] : nodes
  const updatedEdges = [...edges, connectionEdge]

  return {
    updatedNodes,
    updatedEdges,
    createdEndNode: targetEndNode.id.startsWith('end-') ? targetEndNode : undefined,
    createdEdge: connectionEdge,
  }
}

export const ensureFlowIntegrity = (
  nodes: Node[],
  edges: Edge[]
): {updatedNodes: Node[]; updatedEdges: Edge[]; createdEndNode?: Node; createdEdge?: Edge} => {
  console.log('🚀 Ensuring flow integrity...')

  const pathResult = ensureMainPathExists(nodes, edges)

  const cleanupResult = cleanupOrphanedNodesAndEdges(
    pathResult.updatedNodes,
    pathResult.updatedEdges
  )

  if (!validateGraphIntegrity(cleanupResult.updatedNodes, cleanupResult.updatedEdges)) {
    console.warn('🚀 Flow integrity validation failed after cleanup - attempting final fix...')

    const triggerNodes = cleanupResult.updatedNodes.filter((node) => {
      const incomers = getIncomers(node, cleanupResult.updatedNodes, cleanupResult.updatedEdges)
      return incomers.length === 0
    })

    const endNodes = cleanupResult.updatedNodes.filter((node) => {
      const outgoers = getOutgoers(node, cleanupResult.updatedNodes, cleanupResult.updatedEdges)
      return outgoers.length === 0
    })

    if (triggerNodes.length > 0 && endNodes.length > 0) {
      const triggerNode = triggerNodes[0]
      const endNode = endNodes[0]

      const emergencyEdge: Edge = {
        id: `emergency-${triggerNode.id}-${endNode.id}`,
        source: triggerNode.id,
        target: endNode.id,
        type: EDGE_TYPES.SMOOTHSTEP,
        data: {},
      }

      return {
        updatedNodes: cleanupResult.updatedNodes,
        updatedEdges: [...cleanupResult.updatedEdges, emergencyEdge],
        createdEndNode: pathResult.createdEndNode,
        createdEdge: emergencyEdge,
      }
    }
  }

  return {
    updatedNodes: cleanupResult.updatedNodes,
    updatedEdges: cleanupResult.updatedEdges,
    createdEndNode: pathResult.createdEndNode,
    createdEdge: pathResult.createdEdge,
  }
}

/**
 * Removes orphaned edges that are no longer needed
 */
const removeOrphanedEdges = (
  nodes: Node[],
  edges: Edge[]
): {updatedEdges: Edge[]; removedEdges: string[]} => {
  const nodeIds = new Set(nodes.map((node) => node.id))

  const validEdges = edges.filter((edge) => {
    const sourceExists = nodeIds.has(edge.source)
    const targetExists = nodeIds.has(edge.target)
    return sourceExists && targetExists
  })

  const removedEdges = edges
    .filter((edge) => !validEdges.some((e) => e.id === edge.id))
    .map((edge) => edge.id)

  console.log('🚀 Removed orphaned edges:', removedEdges)

  return {updatedEdges: validEdges, removedEdges}
}

/**
 * Removes orphaned nodes that are not connected to any source or target
 */
const removeOrphanedNodes = (
  nodes: Node[],
  edges: Edge[]
): {updatedNodes: Node[]; removedNodes: string[]} => {
  const connectedNodes = new Set<string>()

  // Add all nodes that are sources or targets of edges
  edges.forEach((edge) => {
    connectedNodes.add(edge.source)
    connectedNodes.add(edge.target)
  })

  // Find trigger nodes (nodes with no incoming edges)
  const triggerNodes = nodes.filter((node) => {
    const incomers = getIncomers(node, nodes, edges)
    return incomers.length === 0
  })

  // Add trigger nodes to connected set
  triggerNodes.forEach((node) => connectedNodes.add(node.id))

  // Find nodes that are part of any path from trigger to end
  const pathNodes = findPathNodes(nodes, edges)

  // Add all path nodes to connected set
  pathNodes.forEach((nodeId) => connectedNodes.add(nodeId))

  // Enhanced orphan detection: Find nodes that are truly orphaned
  const orphanedNodes = new Set<string>()

  nodes.forEach((node) => {
    // Skip nodes that are already marked as connected
    if (connectedNodes.has(node.id)) {
      return
    }

    // Check if this node is part of any valid path
    const isPartOfValidPath = pathNodes.has(node.id)

    // Check if this node has any connections
    const incomers = getIncomers(node, nodes, edges)
    const outgoers = getOutgoers(node, nodes, edges)
    const hasConnections = incomers.length > 0 || outgoers.length > 0

    // If node is not part of any valid path and has no meaningful connections, mark as orphaned
    if (!isPartOfValidPath && !hasConnections) {
      orphanedNodes.add(node.id)
    }

    // Also check for nodes that are connected but lead to dead ends (not part of any trigger->end path)
    if (hasConnections && !isPartOfValidPath) {
      // Check if this node leads to any end node through a valid path
      let leadsToEnd = false
      const visited = new Set<string>()
      const queue = [node.id]

      while (queue.length > 0) {
        const currentId = queue.shift()!
        if (visited.has(currentId)) continue
        visited.add(currentId)

        const currentNode = nodes.find((n) => n.id === currentId)
        if (currentNode && currentNode.type === NodeType.END) {
          leadsToEnd = true
          break
        }

        const outgoingEdges = edges.filter((edge) => edge.source === currentId)
        outgoingEdges.forEach((edge) => {
          if (!visited.has(edge.target)) {
            queue.push(edge.target)
          }
        })
      }

      // If this node doesn't lead to any end node, it's orphaned
      if (!leadsToEnd) {
        orphanedNodes.add(node.id)
      }
    }
  })

  console.log('🚀 Orphaned nodes detected:', Array.from(orphanedNodes))

  // Filter nodes, being more aggressive about removing orphaned nodes
  const updatedNodes = nodes.filter((node) => {
    // Always keep connected nodes
    if (connectedNodes.has(node.id)) {
      return true
    }

    // Always keep trigger nodes
    if (node.type === NodeType.TRIGGER) {
      return true
    }

    // Always keep parent nodes (nodes that have edges pointing to other nodes)
    const isParentNode = edges.some((edge) => edge.source === node.id)
    if (isParentNode) {
      console.log('🚀 Preserving parent node:', node.id)
      return true
    }

    // Remove orphaned nodes (including end nodes that are not part of main path)
    if (orphanedNodes.has(node.id)) {
      return false
    }

    // For other nodes, only keep if they're truly connected and part of a valid path
    const incomers = getIncomers(node, nodes, edges)
    const outgoers = getOutgoers(node, nodes, edges)
    const hasConnections = incomers.length > 0 || outgoers.length > 0
    const isPartOfValidPath = pathNodes.has(node.id)

    return hasConnections && isPartOfValidPath
  })

  const removedNodes = nodes
    .filter((node) => !updatedNodes.some((n) => n.id === node.id))
    .map((node) => node.id)

  console.log('🚀 Removed orphaned nodes:', removedNodes)

  return {updatedNodes, removedNodes}
}

/**
 * Validates that there is at least one valid path from trigger to end
 */
const validateGraphIntegrity = (nodes: Node[], edges: Edge[]): boolean => {
  // Find trigger nodes (nodes with no incoming edges)
  const triggerNodes = nodes.filter((node) => {
    const incomers = getIncomers(node, nodes, edges)
    return incomers.length === 0
  })

  // Find end nodes (nodes with no outgoing edges)
  const endNodes = nodes.filter((node) => {
    const outgoers = getOutgoers(node, nodes, edges)
    return outgoers.length === 0
  })

  // Check if there's at least one path from any trigger to any end
  for (const triggerNode of triggerNodes) {
    for (const endNode of endNodes) {
      const visited = new Set<string>()
      const queue = [triggerNode.id]

      while (queue.length > 0) {
        const currentId = queue.shift()!
        if (currentId === endNode.id) {
          return true // Found a valid path
        }

        if (visited.has(currentId)) continue
        visited.add(currentId)

        const outgoingEdges = edges.filter((edge) => edge.source === currentId)
        outgoingEdges.forEach((edge) => {
          if (!visited.has(edge.target)) {
            queue.push(edge.target)
          }
        })
      }
    }
  }

  return false // No valid path found
}

/**
 * Ensures there is at least one valid path from trigger to end
 */
const ensureValidPath = (
  nodes: Node[],
  edges: Edge[]
): {updatedNodes: Node[]; updatedEdges: Edge[]} => {
  // Find all trigger nodes
  const triggerNodes = nodes.filter((node) => {
    const incomers = getIncomers(node, nodes, edges)
    return incomers.length === 0
  })

  // Find all end nodes
  const endNodes = nodes.filter((node) => {
    const outgoers = getOutgoers(node, nodes, edges)
    return outgoers.length === 0
  })

  // If no end nodes exist, create one
  if (endNodes.length === 0) {
    const endNodeId = `end-${Date.now()}`
    const endNode: Node = {
      id: endNodeId,
      type: NodeType.END,
      position: {x: 0, y: 0},
      data: {label: 'End', deletable: true},
    }

    // Connect the last node in the flow to the end node
    const lastNode = nodes[nodes.length - 1]
    if (lastNode) {
      const endEdge: Edge = {
        id: `${lastNode.id}-${endNodeId}`,
        source: lastNode.id,
        target: endNodeId,
        type: EDGE_TYPES.SMOOTHSTEP,
        data: {},
      }

      return {
        updatedNodes: [...nodes, endNode],
        updatedEdges: [...edges, endEdge],
      }
    }
  }

  return {updatedNodes: nodes, updatedEdges: edges}
}

/**
 * Checks if deleting nodes would break the trigger -> end path
 */
const wouldBreakTriggerEndPath = (
  nodes: Node[],
  edges: Edge[],
  nodesToDelete: string[]
): boolean => {
  // Create a copy of nodes and edges without the nodes to delete
  const remainingNodes = nodes.filter((node) => !nodesToDelete.includes(node.id))
  const remainingEdges = edges.filter(
    (edge) => !nodesToDelete.includes(edge.source) && !nodesToDelete.includes(edge.target)
  )

  // Check if there's still a valid path from trigger to end
  return !validateGraphIntegrity(remainingNodes, remainingEdges)
}

/**
 * Deletes a specific branch of a condition node
 */
export const deleteConditionBranch = ({
  nodes,
  edges,
  conditionNodeId,
  branchType,
}: DeleteBranchOptions): DeleteBranchResult => {
  const conditionNode = nodes.find((n) => n.id === conditionNodeId)
  if (!conditionNode || conditionNode.type !== NodeType.CONDITION) {
    throw new Error('Invalid condition node')
  }

  // Preserve parent nodes and edges before deletion
  const {parentNodes, parentEdges} = preserveParentNodes(nodes, edges, conditionNodeId)

  let updatedNodes = [...nodes]
  let updatedEdges = [...edges]
  const deletedNodes: string[] = []
  const deletedEdges: string[] = []

  // Handle different deletion types
  switch (branchType) {
    case 'yes':
      // Delete yes branch only
      const {branchNodes: yesNodes, branchEdges: yesEdges} = findBranchNodesAndEdges(
        nodes,
        edges,
        conditionNodeId,
        EDGE_LABELS.YES
      )

      // Check if deleting yes branch would break trigger -> end path
      const yesNodesToDelete = [...yesNodes.map((n) => n.id), conditionNodeId]
      if (wouldBreakTriggerEndPath(nodes, edges, yesNodesToDelete)) {
        console.warn(
          '🚀 Deleting yes branch would break trigger -> end path, preserving condition node'
        )
        // Only delete the yes branch nodes, keep the condition node
        updatedNodes = updatedNodes.filter(
          (node) => !yesNodes.some((yesNode) => yesNode.id === node.id)
        )
        updatedEdges = updatedEdges.filter(
          (edge) => !yesEdges.some((yesEdge) => yesEdge.id === edge.id)
        )
        deletedNodes.push(...yesNodes.map((node) => node.id))
        deletedEdges.push(...yesEdges.map((edge) => edge.id))
      } else {
        updatedNodes = updatedNodes.filter(
          (node) => !yesNodes.some((yesNode) => yesNode.id === node.id)
        )
        updatedEdges = updatedEdges.filter(
          (edge) => !yesEdges.some((yesEdge) => yesEdge.id === edge.id)
        )

        deletedNodes.push(...yesNodes.map((node) => node.id))
        deletedEdges.push(...yesEdges.map((edge) => edge.id))

        // Remove condition node and reconnect no branch
        const incomers = getIncomers(conditionNode, nodes, edges)
        const noBranchEdges = edges.filter(
          (edge) => edge.source === conditionNodeId && edge.data?.label === EDGE_LABELS.NO
        )

        // Preserve parent edges by keeping their original data
        const parentEdges = edges.filter((edge) => edge.target === conditionNodeId)

        updatedNodes = updatedNodes.filter((node) => node.id !== conditionNodeId)
        updatedEdges = updatedEdges.filter(
          (edge) => edge.source !== conditionNodeId && edge.target !== conditionNodeId
        )

        // Reconnect parent edges to the remaining branch while preserving their data
        incomers.forEach((inNode) => {
          noBranchEdges.forEach((branchEdge) => {
            // Find the original parent edge to preserve its data
            const originalParentEdge = parentEdges.find((edge) => edge.source === inNode.id)

            const newEdge: Edge = {
              id: `${inNode.id}-${branchEdge.target}`,
              source: inNode.id,
              target: branchEdge.target,
              type: EDGE_TYPES.SMOOTHSTEP,
              data: originalParentEdge?.data || {}, // Preserve original parent edge data
            }
            updatedEdges.push(newEdge)
          })
        })

        deletedNodes.push(conditionNodeId)
      }
      break

    case 'no':
      // Delete no branch only
      const {branchNodes: noNodes, branchEdges: noEdges} = findBranchNodesAndEdges(
        nodes,
        edges,
        conditionNodeId,
        EDGE_LABELS.NO
      )

      // Check if deleting no branch would break trigger -> end path
      const noNodesToDelete = [...noNodes.map((n) => n.id), conditionNodeId]
      if (wouldBreakTriggerEndPath(nodes, edges, noNodesToDelete)) {
        console.warn(
          '🚀 Deleting no branch would break trigger -> end path, preserving condition node'
        )
        // Only delete the no branch nodes, keep the condition node
        updatedNodes = updatedNodes.filter(
          (node) => !noNodes.some((noNode) => noNode.id === node.id)
        )
        updatedEdges = updatedEdges.filter(
          (edge) => !noEdges.some((noEdge) => noEdge.id === edge.id)
        )

        deletedNodes.push(...noNodes.map((node) => node.id))
        deletedEdges.push(...noEdges.map((edge) => edge.id))
      } else {
        updatedNodes = updatedNodes.filter(
          (node) => !noNodes.some((noNode) => noNode.id === node.id)
        )
        updatedEdges = updatedEdges.filter(
          (edge) => !noEdges.some((noEdge) => noEdge.id === edge.id)
        )

        deletedNodes.push(...noNodes.map((node) => node.id))
        deletedEdges.push(...noEdges.map((edge) => edge.id))

        // Remove condition node and reconnect yes branch
        const incomersForNo = getIncomers(conditionNode, nodes, edges)
        const yesBranchEdges = edges.filter(
          (edge) => edge.source === conditionNodeId && edge.data?.label === EDGE_LABELS.YES
        )

        // Preserve parent edges by keeping their original data
        const parentEdgesForNo = edges.filter((edge) => edge.target === conditionNodeId)

        updatedNodes = updatedNodes.filter((node) => node.id !== conditionNodeId)
        updatedEdges = updatedEdges.filter(
          (edge) => edge.source !== conditionNodeId && edge.target !== conditionNodeId
        )

        // Reconnect parent edges to the remaining branch while preserving their data
        incomersForNo.forEach((inNode) => {
          yesBranchEdges.forEach((branchEdge) => {
            // Find the original parent edge to preserve its data
            const originalParentEdge = parentEdgesForNo.find((edge) => edge.source === inNode.id)

            const newEdge: Edge = {
              id: `${inNode.id}-${branchEdge.target}`,
              source: inNode.id,
              target: branchEdge.target,
              type: EDGE_TYPES.SMOOTHSTEP,
              data: originalParentEdge?.data || {}, // Preserve original parent edge data
            }
            updatedEdges.push(newEdge)
          })
        })

        deletedNodes.push(conditionNodeId)
      }
      break

    default:
      throw new Error(`Invalid branch type: ${branchType}`)
  }

  // Remove orphaned nodes (including end nodes that are not part of main path)
  const {updatedNodes: cleanedNodes, removedNodes} = removeOrphanedNodes(updatedNodes, updatedEdges)
  deletedNodes.push(...removedNodes)

  // Remove orphaned edges
  const {updatedEdges: cleanedEdges, removedEdges} = removeOrphanedEdges(cleanedNodes, updatedEdges)
  deletedEdges.push(...removedEdges)

  // Ensure flow integrity and fix any path issues
  const integrityResult = ensureFlowIntegrity(cleanedNodes, cleanedEdges)

  // Add any created nodes/edges to the appropriate lists
  if (integrityResult.createdEndNode) {
    console.log('🚀 Auto-created end node:', integrityResult.createdEndNode.id)
  }
  if (integrityResult.createdEdge) {
    console.log('🚀 Auto-created connection:', integrityResult.createdEdge.id)
  }

  // Validate that the final graph has at least one valid path
  if (!validateGraphIntegrity(integrityResult.updatedNodes, integrityResult.updatedEdges)) {
    console.warn('🚀 Graph integrity validation failed - attempting to fix...')

    // Try to create a minimal valid path if validation fails
    const triggerNodes = integrityResult.updatedNodes.filter((node) => {
      const incomers = getIncomers(node, integrityResult.updatedNodes, integrityResult.updatedEdges)
      return incomers.length === 0
    })

    const endNodes = integrityResult.updatedNodes.filter((node) => {
      const outgoers = getOutgoers(node, integrityResult.updatedNodes, integrityResult.updatedEdges)
      return outgoers.length === 0
    })

    if (triggerNodes.length > 0 && endNodes.length > 0) {
      const triggerNode = triggerNodes[0]
      const endNode = endNodes[0]

      const emergencyEdge: Edge = {
        id: `emergency-${triggerNode.id}-${endNode.id}`,
        source: triggerNode.id,
        target: endNode.id,
        type: EDGE_TYPES.SMOOTHSTEP,
        data: {},
      }

      return {
        updatedNodes: integrityResult.updatedNodes,
        updatedEdges: [...integrityResult.updatedEdges, emergencyEdge],
        deletedNodes,
        deletedEdges,
      }
    }
  }

  return {
    updatedNodes: integrityResult.updatedNodes,
    updatedEdges: integrityResult.updatedEdges,
    deletedNodes,
    deletedEdges,
  }
}

export const cleanFlowOrphans = (
  nodes: Node[],
  edges: Edge[]
): {updatedNodes: Node[]; updatedEdges: Edge[]; removedNodes: string[]; removedEdges: string[]} => {
  console.log('🚀 Cleaning up orphaned nodes and edges in flow...')

  // Use the comprehensive cleanup function
  const result = cleanupOrphanedNodesAndEdges(nodes, edges)

  console.log(
    '🚀 Flow cleanup completed. Removed nodes:',
    result.removedNodes.length,
    'Removed edges:',
    result.removedEdges.length
  )

  return result
}

/**
 * Comprehensive cleanup function that removes all orphaned nodes and edges
 */
export const cleanupOrphanedNodesAndEdges = (
  nodes: Node[],
  edges: Edge[]
): {updatedNodes: Node[]; updatedEdges: Edge[]; removedNodes: string[]; removedEdges: string[]} => {
  console.log('🚀 Starting comprehensive cleanup of orphaned nodes and edges...')

  // First, remove orphaned nodes
  const {updatedNodes: cleanedNodes, removedNodes} = removeOrphanedNodes(nodes, edges)

  // Then, remove orphaned edges
  const {updatedEdges: cleanedEdges, removedEdges} = removeOrphanedEdges(cleanedNodes, edges)

  // Ensure valid path exists
  const {updatedNodes: pathNodes, updatedEdges: pathEdges} = ensureValidPath(
    cleanedNodes,
    cleanedEdges
  )

  // Validate that the final graph has at least one valid path
  if (!validateGraphIntegrity(pathNodes, pathEdges)) {
    console.warn('🚀 Graph integrity validation failed after cleanup - attempting to fix...')

    // Try to create a minimal valid path if validation fails
    const triggerNodes = pathNodes.filter((node) => {
      const incomers = getIncomers(node, pathNodes, pathEdges)
      return incomers.length === 0
    })

    const endNodes = pathNodes.filter((node) => {
      const outgoers = getOutgoers(node, pathNodes, pathEdges)
      return outgoers.length === 0
    })

    if (triggerNodes.length > 0 && endNodes.length > 0) {
      const triggerNode = triggerNodes[0]
      const endNode = endNodes[0]

      const emergencyEdge: Edge = {
        id: `emergency-${triggerNode.id}-${endNode.id}`,
        source: triggerNode.id,
        target: endNode.id,
        type: EDGE_TYPES.SMOOTHSTEP,
        data: {},
      }

      return {
        updatedNodes: pathNodes,
        updatedEdges: [...pathEdges, emergencyEdge],
        removedNodes,
        removedEdges,
      }
    }
  }

  console.log(
    '🚀 Cleanup completed. Removed nodes:',
    removedNodes.length,
    'Removed edges:',
    removedEdges.length
  )

  return {
    updatedNodes: pathNodes,
    updatedEdges: pathEdges,
    removedNodes,
    removedEdges,
  }
}

/**
 * Gets the available deletion options for a condition node
 */
export const getConditionDeleteOptions = (
  nodes: Node[],
  edges: Edge[],
  conditionNodeId: string
): BranchType[] => {
  const yesEdges = edges.filter(
    (edge) => edge.source === conditionNodeId && edge.data?.label === EDGE_LABELS.YES
  )
  const noEdges = edges.filter(
    (edge) => edge.source === conditionNodeId && edge.data?.label === EDGE_LABELS.NO
  )

  const options: BranchType[] = []

  if (yesEdges.length > 0) options.push('yes')
  if (noEdges.length > 0) options.push('no')

  return options
}

/**
 * Checks flow integrity and provides detailed information about the flow state
 */
export const checkFlowIntegrity = (
  nodes: Node[],
  edges: Edge[]
): {
  hasValidPath: boolean
  triggerNodes: Node[]
  endNodes: Node[]
  disconnectedNodes: Node[]
  orphanedNodes: string[]
  orphanedEdges: string[]
  suggestions: string[]
} => {
  console.log('🚀 Checking flow integrity...')

  // Check if flow has valid main path
  const hasValidPath = hasValidMainPath(nodes, edges)

  // Find trigger nodes
  const triggerNodes = nodes.filter((node) => {
    const incomers = getIncomers(node, nodes, edges)
    return incomers.length === 0
  })

  // Find end nodes
  const endNodes = nodes.filter((node) => {
    const outgoers = getOutgoers(node, nodes, edges)
    return outgoers.length === 0
  })

  // Find disconnected nodes
  const disconnectedNodes = findDisconnectedNodes(nodes, edges)

  // Find orphaned nodes and edges
  const {removedNodes: orphanedNodes, removedEdges: orphanedEdges} = cleanupOrphanedNodesAndEdges(
    nodes,
    edges
  )

  // Generate suggestions
  const suggestions: string[] = []

  if (!hasValidPath) {
    suggestions.push('No valid path from trigger to end detected')
    if (triggerNodes.length === 0) {
      suggestions.push('No trigger nodes found')
    }
    if (endNodes.length === 0) {
      suggestions.push('No end nodes found')
    }
    if (disconnectedNodes.length > 0) {
      suggestions.push(`${disconnectedNodes.length} disconnected nodes found`)
    }
    suggestions.push('Consider using ensureFlowIntegrity() to fix the flow')
  }

  if (orphanedNodes.length > 0) {
    suggestions.push(`${orphanedNodes.length} orphaned nodes detected`)
  }

  if (orphanedEdges.length > 0) {
    suggestions.push(`${orphanedEdges.length} orphaned edges detected`)
  }

  console.log('🚀 Flow integrity check completed:', {
    hasValidPath,
    triggerNodes: triggerNodes.length,
    endNodes: endNodes.length,
    disconnectedNodes: disconnectedNodes.length,
    orphanedNodes: orphanedNodes.length,
    orphanedEdges: orphanedEdges.length,
  })

  return {
    hasValidPath,
    triggerNodes,
    endNodes,
    disconnectedNodes,
    orphanedNodes,
    orphanedEdges,
    suggestions,
  }
}
